import { EditIcon, PauseIcon, PencilIcon, PlayIcon } from 'lucide-react'
import { useCallback, useState } from 'react'

import { InstrumentDto, SetInstrumentStateDto, useSetClientMarketInstrumentState } from '@/gen/publicApi'

import PlaceOrderDialog from '@/containers/placeOrderDialog'

import { DropdownMenuItem } from '@/components/dropdown-menu'
import OptionMenu from '@/components/optionMenu'
import { useToast } from '@/components/toast/useToast'

interface Props {
    clientCode: string
    marketCode: string
    instrument: InstrumentDto
    onChange: (instrument: InstrumentDto) => void
}

export default function InstrumentOptionMenu({ instrument, clientCode, marketCode, onChange }: Props) {
    const { toast } = useToast()
    const [placeOrderOpen, setPlaceOrderOpen] = useState(false)
    const setStateMutation = useSetClientMarketInstrumentState(clientCode, marketCode, instrument.symbol)
    const handleStateChange = useCallback(
        (newState: SetInstrumentStateDto['state']) => {
            // I'm changing the state here for the immediate update of the UI, but
            // this may actually take a bit on the server.
            onChange({ ...instrument, state: newState })
            setStateMutation.mutate({ state: newState })
        },
        [instrument, onChange, setStateMutation],
    )

    const onPlaceOrderSuccess = () => {
        setPlaceOrderOpen(false)
        toast({
            title: 'Order placed successfully!',
            description: 'It may take a few seconds to appear',
        })
    }

    const Icon = instrument.state === 'Open' ? PauseIcon : PlayIcon
    const text = instrument.state === 'Open' ? 'Halt' : 'Open'
    const alteredState = instrument.state === 'Open' ? 'Halt' : 'Open'

    return (
        <>
            <OptionMenu>
                <DropdownMenuItem className="flex">
                    <EditIcon className="w-4 h-4 mr-2" /> Edit
                </DropdownMenuItem>
                <DropdownMenuItem className="flex" onClick={() => handleStateChange(alteredState)}>
                    <Icon className="w-4 h-4 mr-2" /> {text}
                </DropdownMenuItem>
                <DropdownMenuItem className="flex" onClick={() => setPlaceOrderOpen(true)}>
                    <PencilIcon className="w-4 h-4 mr-2" /> Place an order
                </DropdownMenuItem>
            </OptionMenu>
            {placeOrderOpen && (
                <PlaceOrderDialog
                    clientCode={clientCode}
                    marketCode={marketCode}
                    onOpenChange={setPlaceOrderOpen}
                    onSuccess={onPlaceOrderSuccess}
                    instrumentSymbol={instrument.symbol}
                />
            )}
        </>
    )
}
